import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { Card } from "@speakai/ui/card";

interface FloatingActionCardProps {
  title: string;
  subtitle?: string;
  onDismiss: () => void;
  /** Makes the title area a button (e.g. open the linked page). */
  onOpen?: () => void;
  action?: ReactNode;
  testId?: string;
}

/** Card in the bottom-right floating stack: title, optional subtitle and action, dismiss button. */
export function FloatingActionCard({ title, subtitle, onDismiss, onOpen, action, testId }: FloatingActionCardProps) {
  const { t } = useTranslation();
  const text = (
    <>
      <p className="truncate text-sm font-semibold text-foreground">{title}</p>
      {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
    </>
  );

  return (
    <Card className="pointer-events-auto flex flex-col gap-3 p-4 shadow-lg" data-testid={testId}>
      <div className="flex items-start justify-between gap-3">
        {onOpen ? (
          <button type="button" onClick={onOpen} className="min-w-0 flex-1 text-left">
            {text}
          </button>
        ) : (
          <div className="min-w-0">{text}</div>
        )}
        <button
          type="button"
          onClick={onDismiss}
          aria-label={t("ACTION.DISMISS", "Dismiss")}
          className="shrink-0 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      {action && <div className="flex gap-2">{action}</div>}
    </Card>
  );
}
